import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GoogleLogo, EnvelopeSimple, Eye, EyeSlash } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const { loginWithEmail, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await loginWithEmail(email, password);
      } else {
        await register(email, password, name);
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Scroll focused input into view on mobile when keyboard opens
  const handleFocus = (e) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#FAF7F2] flex" data-testid="login-page">
      {/* Left — Hero (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-[#2D6A4F]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#84A98C] blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-[#CDB4DB] blur-[100px]" />
        </div>
        <div className="relative z-10 text-center px-12 max-w-lg">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="mx-auto mb-8 w-36 h-36 rounded-3xl bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-black/10 border border-white/50"
          >
            <img src="/logo.png" alt="Mood Chemistry Lab" className="h-28 w-auto object-contain" />
          </motion.div>
          <p className="text-white/70 text-base leading-relaxed font-['Figtree']">
            Discover your emotional formula. Shift your state in real time with guided sensory experiences by Coach KB.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            {["#84A98C", "#CDB4DB", "#F4A261", "#2D6A4F"].map((c, i) => (
              <motion.div
                key={c}
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, delay: i * 0.4, repeat: Infinity }}
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right — Form (scrollable on mobile) */}
      <div className="flex-1 overflow-y-auto" ref={formRef}>
        <div className="flex items-start lg:items-center justify-center min-h-screen min-h-[100dvh] p-6 sm:p-12">
          <div className="w-full max-w-md py-8 sm:py-0">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src="/logo.png" alt="Mood Chemistry Lab" className="h-10 w-auto" />
              <span className="font-['Outfit'] font-semibold text-[#1A2421] text-lg">Mood Chemistry Lab</span>
            </div>

            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-medium text-[#1A2421] mb-1">
              {mode === "login" ? "Welcome back" : "Start your journey"}
            </h2>
            <p className="text-[#78857F] text-sm mb-6">
              {mode === "login" ? "Sign in to continue your wellness practice" : "Create your account to begin"}
            </p>

            {/* Google Auth */}
            <Button
              onClick={loginWithGoogle}
              data-testid="google-login-button"
              type="button"
              variant="outline"
              className="w-full rounded-full h-12 border-[#E1D9CD] hover:border-[#2D6A4F] hover:bg-[#2D6A4F]/5 text-[#1A2421] font-medium transition-all duration-300 mb-5"
            >
              <GoogleLogo size={20} weight="bold" className="mr-2 text-[#2D6A4F]" />
              Continue with Google
            </Button>

            <div className="relative mb-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E1D9CD]" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#FAF7F2] px-4 text-[#78857F]">or</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <AnimatePresence mode="wait">
                {mode === "register" && (
                  <motion.div
                    key="name"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <Label className="text-xs text-[#78857F] mb-1 block">Full Name</Label>
                    <Input
                      data-testid="register-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onFocus={handleFocus}
                      placeholder="Your name"
                      autoComplete="name"
                      enterKeyHint="next"
                      className="rounded-xl h-12 border-[#E1D9CD] bg-white focus:border-[#2D6A4F] focus:ring-[#84A98C] text-base"
                      required
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <Label className="text-xs text-[#78857F] mb-1 block">Email</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78857F] pointer-events-none" />
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={handleFocus}
                    placeholder="you@email.com"
                    autoComplete="email"
                    enterKeyHint="next"
                    inputMode="email"
                    className="rounded-xl h-12 pl-10 border-[#E1D9CD] bg-white focus:border-[#2D6A4F] focus:ring-[#84A98C] text-base"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#78857F] mb-1 block">Password</Label>
                <div className="relative">
                  <Input
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={handleFocus}
                    placeholder="Min 6 characters"
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    enterKeyHint="done"
                    className="rounded-xl h-12 pr-10 border-[#E1D9CD] bg-white focus:border-[#2D6A4F] focus:ring-[#84A98C] text-base"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78857F] hover:text-[#2D6A4F] p-1"
                  >
                    {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg" data-testid="auth-error">{error}</p>
              )}

              <div className="pt-1">
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid={mode === "login" ? "login-form-submit-button" : "register-form-submit-button"}
                  className="w-full rounded-full h-12 bg-[#2D6A4F] hover:bg-[#1B4936] text-white font-medium text-base transition-all duration-300 shadow-md"
                >
                  {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
                </Button>
              </div>
            </form>

            <p className="text-center text-sm text-[#78857F] mt-5">
              {mode === "login" ? "New to Mood Chemistry Lab?" : "Already have an account?"}{" "}
              <button
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                data-testid="toggle-auth-mode"
                className="text-[#2D6A4F] font-medium hover:underline"
              >
                {mode === "login" ? "Create account" : "Sign in"}
              </button>
            </p>

            <p className="text-center text-[10px] text-[#78857F]/60 mt-6 pb-8">
              by Coach KB (Ceal Starks) &middot; The Sensory Reset&trade;
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
